import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'

export interface CouponCourse {
  couponId: string
  courseId: number
  course: { id: number; title: string }
}

export interface Coupon {
  id: string
  code: string
  companyId: string
  company: { id: string; name: string }
  allCourses: boolean
  active: boolean
  maxUses: number | null
  usedCount: number
  expiresAt: string
  createdAt: string
  updatedAt: string
  coupon_courses: CouponCourse[]
}

export interface CreateCouponDTO {
  code: string
  companyId: string
  allCourses?: boolean
  courseIds?: number[]
  maxUses?: number | null
  expiresAt: string
}

export interface UpdateCouponDTO {
  code?: string
  companyId?: string
  allCourses?: boolean
  courseIds?: number[]
  maxUses?: number | null
  active?: boolean
  expiresAt?: string
}

export function useAdminCoupons(enabled = true) {
  return useQuery({
    queryKey: ['admin-coupons'],
    queryFn: async () => {
      const { data } = await api.get<Coupon[]>('/admin/coupons')
      return data
    },
    enabled,
  })
}

export function useCreateCoupon() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dto: CreateCouponDTO) => {
      const { data } = await api.post<Coupon>('/admin/coupons', dto)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] })
    },
  })
}

export function useUpdateCoupon() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...dto }: UpdateCouponDTO & { id: string }) => {
      const { data } = await api.put<Coupon>(`/admin/coupons/${id}`, dto)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] })
    },
  })
}

export function useDeactivateCoupon() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/coupons/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] })
    },
  })
}
